import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as pdfjsLib from 'pdfjs-dist';
import styled from 'styled-components';
import PrimaryButtonComponent from '../../Components/Buttons/PrimaryButton/PrimaryButton.component';
import SecondaryButton from '../../Components/Buttons/SecondaryButton/SecondaryButton.component';
import InfoCard from '../../Components/InfoCard/InfoCard.component';
import { ButtonContainerPresentation } from './CourseHome.styles';
// Configure the worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@5.1.91/build/pdf.worker.min.mjs`;

// Progress bar styles
const ProgressBarContainer = styled.div`
  width: 100%;
  height: 10px;
  background-color: #e0e0e0;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const ProgressBar = styled.div`
  width: ${({ progress }) => progress}%;
  height: 100%;
  background-color: var(--orange4);
  transition: width 0.2s ease;
`;

// Full-screen modal styles
const FullScreenModal = styled.div`
  position: fixed;
  padding: 10rem;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @media screen and (max-width: 768px) {
    padding: 4rem;
  }
  @media screen and (max-width: 480px) {
    padding: 2rem;
  }
`;

const PDFViewer = ({ pdfUrl, showIframe }) => {
  const canvasRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [inputPage, setInputPage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [progress, setProgress] = useState(0); // Track loading progress
  const [isFullScreen, setIsFullScreen] = useState(false); // Track full-screen mode
  const renderTaskRef = useRef(null);

  const reloadPdf = () => {
    setError(false);
    setPdf(pdfUrl);
    setCurrentPage(1);
    setTotalPages(0);
    setLoading(true);
    setProgress(0);
  };

  useEffect(() => {
    const loadPdf = async (retryCount = 5, delay = 1000) => {
      try {
        setLoading(true);
        setError(false);
        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          rangeChunkSize: 65536,
        });
        loadingTask.onProgress = (progressData) => {
          const percent = Math.min(
            (progressData.loaded / progressData.total) * 100,
            100
          );
          setProgress(percent);
        };
        const loadedPdf = await loadingTask.promise;
        setPdf(loadedPdf);
        setTotalPages(loadedPdf.numPages);
        renderPage(1, loadedPdf);
      } catch (err) {
        console.error(
          `Error loading PDF (attempt ${6 - retryCount}):`,
          err.message
        );
        if (retryCount > 0) {
          setTimeout(() => loadPdf(retryCount - 1, delay * 2), delay);
        } else {
          setError(true);
        }
      } finally {
        setLoading(false);
      }
    };
    loadPdf();
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (error) {
      reloadPdf();
    }
  }, [error]);

  const renderPage = async (pageNumber, loadedPdf = pdf) => {
    if (!loadedPdf) return;

    try {
      setLoading(true);

      // Cancel any ongoing render task
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }

      const page = await loadedPdf.getPage(pageNumber);

      // Create a new canvas element dynamically
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const viewport = page.getViewport({ scale: isFullScreen ? 2 : 1.5 });

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext = {
        canvasContext: context,
        viewport,
      };

      // Append the new canvas to the container
      const container = canvasRef.current;
      container.innerHTML = ''; // Clear previous canvas
      container.appendChild(canvas);

      // Start rendering the page
      renderTaskRef.current = page.render(renderContext);
      await renderTaskRef.current.promise;
    } catch (err) {
      if (err.name !== 'RenderingCancelledException') {
        console.error('Error rendering page:', err);
        setError(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showIframe && pdf) {
      renderPage(currentPage);
    }
  }, [showIframe, pdf, currentPage]);

  const handleNext = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      renderPage(nextPage);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      renderPage(prevPage);
    }
  };

  const handleInputChange = (e) => {
    setInputPage(e.target.value);
  };

  const handleGoToPage = () => {
    const pageNumber = parseInt(inputPage, 10);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      renderPage(pageNumber);
    } else {
      alert(`Please enter a valid page number between 1 and ${totalPages}`);
    }
  };

  const toggleFullScreen = () => {
    setIsFullScreen((prevState) => {
      const newFullScreenState = !prevState;
      if (pdf) {
        renderPage(currentPage, pdf);
      }
      return newFullScreenState;
    });
  };

  if (!pdfUrl) {
    return (
      <div style={{ textAlign: 'center', marginTop: '1rem' }}>
        <p>No PDF provided.</p>
      </div>
    );
  }
  if (error) {
    return (
      <div
        style={{
          textAlign: 'center',
          marginTop: '1rem',
        }}
      >
        <iframe
          src={`${pdfUrl}#toolbar=0`}
          width="100%"
          height="600px"
          style={{ border: 'none' }}
          title="PDF Viewer"
        ></iframe>
        <InfoCard
          paragraph="Unable to load the PDF with enhanced features. Displaying in fallback
          mode. (This mode does not work on tablet and mobile phones, please
          reload the presentation)"
        />
        <PrimaryButtonComponent
          span="Reload Presentation"
          functionName={reloadPdf}
        />
      </div>
    );
  }

  const ViewerContent = (
    <div
      style={{
        textAlign: 'center',
        marginTop: '1rem',
      }}
    >
      {loading && (
        <>
          <ProgressBarContainer>
            <ProgressBar progress={progress} />
          </ProgressBarContainer>
        </>
      )}
      <div ref={canvasRef} onDoubleClick={toggleFullScreen}></div>
      <span className="largeSpan italicSpan m1T">
        Double click the presentation for full screen mode
      </span>
      <ButtonContainerPresentation>
        <PrimaryButtonComponent
          functionName={handlePrevious}
          disabled={currentPage === 1}
          span="Previous"
        />
        <p style={{ margin: '0 10px' }}>
          Page {currentPage} of {totalPages || '...'}
        </p>
        <PrimaryButtonComponent
          functionName={handleNext}
          disabled={currentPage === totalPages}
          span="Next"
        />
      </ButtonContainerPresentation>
      <div className="m1B m1T">
        <input
          type="number"
          value={inputPage}
          onChange={handleInputChange}
          placeholder="Enter page number"
          style={{ padding: '12px', width: '155px', marginRight: '10px' }}
        />
        <SecondaryButton functionName={handleGoToPage} span="Select Page" />
      </div>
    </div>
  );

  return (
    <div>
      {showIframe ? (
        <iframe
          src={`${pdfUrl}#toolbar=0`}
          width="100%"
          height="600px"
          style={{ border: 'none', marginTop: '20px' }}
          title="PDF Viewer"
        ></iframe>
      ) : isFullScreen ? (
        <FullScreenModal>
          <SecondaryButton functionName={toggleFullScreen} span="Close" />
          {ViewerContent}
        </FullScreenModal>
      ) : (
        ViewerContent
      )}
    </div>
  );
};

PDFViewer.propTypes = {
  pdfUrl: PropTypes.string,
  showIframe: PropTypes.bool,
};

export default PDFViewer;
